import React, { useEffect, useRef, useState } from "react";
import tw from "tailwind-styled-components";
import {
    InputGroup,
    SideCancelBtn,
    SubmitBtn,
    ActionSection,
    ScrollableContent,
    Wrapper,
    FieldWrappers,
    CardWrapper,
    SelectionCard,
    CombineInputGroup,
    ModalHeader,
} from "../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import { Formik, Form, Field } from "formik";
import { useSelector } from "react-redux";
import CategoryCard from "../CategoryCard";
import SinglePropSelectModal from "./SinglePropSelectModal";
import Config from "../../Config";
import axios from "axios";
import { useQuery } from "react-query";
import SelectRoom from "./SelectRoom";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import moment from "moment";
import { FaRegCalendar } from "react-icons/fa";
import { FormControl, MenuItem, Select } from "@mui/material";

const ReassignTenant = ({
    selectedData,
    handleClose,
    reassignTenantLoading,
    reassignTenantMutate,
    tenantId
}) => {
    const user = useSelector((state) => state.UserReducer.user);
    const propertyList = useSelector((state) => state.PropertyReducer.property);
    const formikRef = useRef(null);
    const [showPropertyModal, setShowPropertyModal] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [roomModalOpen, setRoomModalOpen] = useState(false);
    const [roomList, setRoomList] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [height, setHeight] = useState(window.innerHeight);
    const [openCalendar, setOpenCalendar] = useState(null);
    const [moveInDate, setMoveInDate] = useState(new Date());

    const rentalCycleOptions = [
        { label: "1st to 1st", value: "01" },
        { label: "15th to 15th", value: "15" },
        { label: "Move-In Date", value: "00" },
    ];

    // Yup Validation Schema
    const validationSchema = Yup.object().shape({
        property: Yup.string().required("Please select a property"),
        roomId: Yup.string().required("Please select a room"),
        moveInDate: Yup.date()
            .required("Move-in date is required"),
        rentalCycle: Yup.string().required("Rental cycle is required"),
        agreementPeriod: Yup.number()
            .required("Agreement period is required")
            .min(1, "Agreement period must be at least 1 month")
            .max(60, "Agreement period cannot exceed 60 months")
            .integer("Agreement period must be a whole number")
            .typeError("Please enter a valid number"),
        noticePeriod: Yup.number()
            .required("Notice period is required")
            .min(0, "Notice period must be selected")
            .integer("Notice period must be a whole number")
            .typeError("Please select a valid notice period"),
        lockInPeriod: Yup.number()
            .required("Lock-in period is required")
            .min(1, "Lock-in period must be at least 1 month")
            .integer("Lock-in period must be a whole number")
            .typeError("Please select a valid lock-in period"),
        discardDues: Yup.boolean()
            .required("Please select an option for managing previous dues")
            .test(
                'is-selected',
                'Please select an option for managing previous dues',
                value => value === true || value === false
            ),
    });

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const fetchListing = async () => {
        if (!selectedProperty) return;
        const response = await axios.get(
            `${Config.apiUrl}/room/available/list/${selectedProperty?.id}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user.token}`,
                },
            }
        );
        return response.data;
    };

    const {
        isLoading,
        data: roomData,
        refetch,
        error,
    } = useQuery(["roomData", selectedProperty], fetchListing, {
        enabled: false,
        onSuccess: (data) => {
            if (data && data.data) {
                setRoomList(data.data);
            } else {
                setRoomList([]);
            }
        },
        onError: (error) => {
            console.error("Error fetching rooms:", error);
            setRoomList([]);
        },
    });

    useEffect(() => {
        setSelectedRoom(null);
        setRoomList([]);

        if (selectedProperty) {
            refetch();
        }
    }, [selectedProperty, refetch]);

    const initialValues = {
        property: selectedProperty ? selectedProperty.id : "",
        roomId: "",
        moveInDate: moveInDate,
        rentalCycle: "01",
        agreementPeriod: 11,
        noticePeriod: 1,
        lockInPeriod: 1,
        discardDues: null,
        tenantId: selectedData?.tenantId || "",
        propId: selectedProperty?.id || "",
    };

    useEffect(() => {
        if (formikRef.current && selectedRoom && selectedRoom.length > 0) {
            formikRef.current.setFieldValue("roomId", selectedRoom[0].id);
        } else if (formikRef.current) {
            formikRef.current.setFieldValue("roomId", "");
        }
    }, [selectedRoom]);

    useEffect(() => {
        if (formikRef.current && selectedProperty) {
            formikRef.current.setFieldValue("property", selectedProperty.id);
            formikRef.current.setFieldValue("propId", selectedProperty.id);
        }
    }, [selectedProperty]);

    const handleSubmit = (values, { setSubmitting, resetForm }) => {
        if (!selectedRoom || selectedRoom.length === 0) {
            formikRef.current.setFieldError("roomId", "Please select a room");
            setSubmitting(false);
            return;
        }

        const apiPayload = {
            tenantId: tenantId,
            propId: values.property,
            roomId: values.roomId,
            moveInDate: moment(values.moveInDate).format("YYYY-MM-DD"),
            rentalCycle: values.rentalCycle,
            agreementPeriod: parseInt(values.agreementPeriod),
            noticePeriod: parseInt(values.noticePeriod),
            lockInPeriod: parseInt(values.lockInPeriod),
            carryForwardDues: values.discardDues === true ? 0 : 1,
            resetKyc: 1
        };

        reassignTenantMutate(apiPayload);
    };

    const filteredProperties = propertyList.filter(
        (property) => property.id !== 0
    );

    const handleSelectedProperty = (selectedOption) => {
        setSelectedProperty(selectedOption);
        setSelectedRoom(null);
        if (formikRef.current) {
            formikRef.current.setFieldValue("property", selectedOption.id);
            formikRef.current.setFieldValue("propId", selectedOption.id);
            formikRef.current.setFieldValue("roomId", "");
        }
    };

    const handleSingleRoomSelect = (room, setFieldValue) => {
        if (room) {
            setFieldValue("roomId", room.id);
            setSelectedRoom([room]);
        } else {
            setFieldValue("roomId", "");
            setSelectedRoom(null);
        }
        setRoomModalOpen(false);
    };

    const renderSelectedRoom = (selected = []) => {
        if (!selected || !selected.length) return "Select Room";

        const maxToShow = 2;
        const namesToShow = selected
            .slice(0, maxToShow)
            .map((room) => room.roomNum);
        const remainingCount = selected.length - maxToShow;

        return (
            <div className="flex text-left">
                <span>{namesToShow.join(", ")}</span>
                {remainingCount > 0 && (
                    <span className="ml-1">+{remainingCount} more</span>
                )}
            </div>
        );
    };

    return (
        <>
            <Container
                style={{
                    height: `${height}px`,
                    overflow: "auto",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                <ModalHeader>
                    <h1 className="text-white text-lg font-medium">Reassign Tenant</h1>
                    <button onClick={handleClose}>
                        <MdClose color="white" size={24} />
                    </button>
                </ModalHeader>

                <div className="px-6 h-full flex flex-col justify-between">
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        innerRef={formikRef}
                        // Remove enableReinitialize to prevent reinitialization on room selection
                        enableReinitialize={false}
                    >
                        {({ values, handleChange, setFieldValue, errors, touched, isSubmitting }) => {
                            return (
                                <Form>
                                    <ScrollableContent className="max-h-[calc(100vh-140px)]">
                                        <Wrapper>
                                            {filteredProperties &&
                                                filteredProperties.length > 0 && (
                                                    <InputGroup>
                                                        <Label>Select property *</Label>
                                                        <CategoryCard
                                                            title="Property"
                                                            selectedCategory={selectedProperty}
                                                            onClick={() => setShowPropertyModal(true)}
                                                        />
                                                        {errors.property && touched.property && (
                                                            <div className="text-red-500 text-sm mt-1">
                                                                {errors.property}
                                                            </div>
                                                        )}
                                                    </InputGroup>
                                                )}

                                            <InputGroup>
                                                <Label>Select Room *</Label>
                                                {selectedProperty ? (
                                                    roomList.length > 0 ? (
                                                        <>
                                                            <CategoryCard
                                                                title="Room"
                                                                selectedCategory={
                                                                    selectedRoom && selectedRoom.length > 0
                                                                        ? {
                                                                            label:
                                                                                renderSelectedRoom(selectedRoom),
                                                                            items: selectedRoom,
                                                                        }
                                                                        : null
                                                                }
                                                                onClick={() => setRoomModalOpen(true)}
                                                            />
                                                            {errors.roomId && touched.roomId && (
                                                                <div className="text-red-500 text-sm mt-1">
                                                                    {errors.roomId}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="w-full h-11 px-2 py-3 items-center border border-gray-200 rounded-lg bg-gray-50 text-center">
                                                            <p className="text-gray-500 text-sm">
                                                                {isLoading
                                                                    ? "Loading rooms..."
                                                                    : "No rooms available in this property"}
                                                            </p>
                                                        </div>
                                                    )
                                                ) : (
                                                    <div className="w-full h-11 px-2 py-3 items-center border border-gray-200 rounded-lg bg-gray-50 text-center">
                                                        <p className="text-gray-500 text-sm">
                                                            Please select a property first
                                                        </p>
                                                    </div>
                                                )}
                                            </InputGroup>

                                            <InputGroup>
                                                <Label htmlFor="moveInDate">
                                                    Move-In Date *
                                                </Label>
                                                <FieldWrappers>
                                                    <DatePicker
                                                        selected={values.moveInDate}
                                                        onChange={(date) => {
                                                            setFieldValue("moveInDate", date);
                                                            setOpenCalendar(null);
                                                        }}
                                                        onClickOutside={() => setOpenCalendar(null)}
                                                        onSelect={() => setOpenCalendar(null)}
                                                        open={openCalendar === 'moveIn'}
                                                        onInputClick={() => {
                                                            setOpenCalendar(openCalendar === 'moveIn' ? null : 'moveIn');
                                                        }}
                                                        dateFormat="yyyy-MM-dd"
                                                        placeholderText="YYYY-MM-DD"
                                                        className="w-full cursor-pointer"
                                                        showPopperArrow={false}
                                                        showIcon
                                                        icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                        wrapperClassName="w-full"
                                                        name="moveInDate"
                                                        id="moveInDate"
                                                        autoComplete="off"
                                                        portalId="root-portal"
                                                        onKeyDown={(e) => e.preventDefault()}
                                                    />

                                                </FieldWrappers>
                                                {touched.moveInDate && errors.moveInDate && (
                                                    <p className="text-red-500 text-sm mt-1">
                                                        {errors.moveInDate}
                                                    </p>
                                                )}
                                            </InputGroup>

                                            <InputGroup>
                                                <Label>Rental Cycle *</Label>
                                                <CardWrapper>
                                                    {rentalCycleOptions.map((mode) => (
                                                        <SelectionCard
                                                            key={mode.value}
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setFieldValue("rentalCycle", mode.value, false);
                                                            }}
                                                            className={values.rentalCycle === mode.value
                                                                ? "bg-[#0078BD] text-white px-4 lg:px-6"
                                                                : "bg-gray-100 text-gray-600 px-4 lg:px-6"}
                                                        >
                                                            <span className="text-sm">{mode.label}</span>
                                                        </SelectionCard>
                                                    ))}
                                                </CardWrapper>
                                                {touched.rentalCycle && errors.rentalCycle && (
                                                    <p className="text-red-500 text-sm mt-1">
                                                        {errors.rentalCycle}
                                                    </p>
                                                )}
                                            </InputGroup>

                                            <InputGroup>
                                                <Label htmlFor="agreementPeriod">Agreement Period (Months) *</Label>
                                                <FieldWrappers>
                                                    <Field
                                                        type="number"
                                                        name="agreementPeriod"
                                                        id="agreementPeriod"
                                                        autoComplete="off"
                                                        placeholder="Enter agreement period (1-60 months)"
                                                        min="1"
                                                        max="60"
                                                        required
                                                        onChange={(e) => {
                                                            const value = parseInt(e.target.value);
                                                            if (value > 60) {
                                                                setFieldValue("agreementPeriod", 60);
                                                            } else if (value < 1) {
                                                                setFieldValue("agreementPeriod", 1);
                                                            } else {
                                                                setFieldValue("agreementPeriod", value);
                                                            }
                                                        }}
                                                    />
                                                </FieldWrappers>
                                                {touched.agreementPeriod && errors.agreementPeriod && (
                                                    <p className="text-red-500 text-sm mt-1">
                                                        {errors.agreementPeriod}
                                                    </p>
                                                )}
                                            </InputGroup>

                                            <CombineInputGroup>
                                                <InputGroup>
                                                    <Label htmlFor="noticePeriod">Notice Period *</Label>
                                                    <FormControl fullWidth>
                                                        <Select
                                                            className="px-2"
                                                            id="noticePeriod"
                                                            name="noticePeriod"
                                                            value={values.noticePeriod}
                                                            onChange={(e) => setFieldValue("noticePeriod", e.target.value)}
                                                            required
                                                            style={{ height: "46px", borderColor: '#E5E7EB' }}
                                                            error={touched.noticePeriod && Boolean(errors.noticePeriod)}
                                                        >
                                                            <MenuItem value="">
                                                                Select notice period
                                                            </MenuItem>
                                                            {Array.from({ length: 7 }, (_, i) => i).map(
                                                                (month) => (
                                                                    <MenuItem key={month} value={month}>
                                                                        {month} {month === 1 ? "month" : "months"}
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                    {touched.noticePeriod && errors.noticePeriod && (
                                                        <p className="text-red-500 text-sm mt-1">
                                                            {errors.noticePeriod}
                                                        </p>
                                                    )}
                                                </InputGroup>

                                                <InputGroup>
                                                    <Label htmlFor="lockInPeriod">Lock-In Period *</Label>
                                                    <FormControl fullWidth>
                                                        <Select
                                                            className="px-2"
                                                            id="lockInPeriod"
                                                            name="lockInPeriod"
                                                            value={values.lockInPeriod}
                                                            onChange={(e) => setFieldValue("lockInPeriod", e.target.value)}
                                                            required
                                                            style={{ height: "46px", borderColor: '#E5E7EB' }}
                                                            error={touched.lockInPeriod && Boolean(errors.lockInPeriod)}
                                                        >
                                                            <MenuItem value="">
                                                                Select lock-in period
                                                            </MenuItem>
                                                            {Array.from({ length: 12 }, (_, i) => i + 1).map(
                                                                (month) => (
                                                                    <MenuItem key={month} value={month}>
                                                                        {month} {month === 1 ? "month" : "months"}
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                    {touched.lockInPeriod && errors.lockInPeriod && (
                                                        <p className="text-red-500 text-sm mt-1">
                                                            {errors.lockInPeriod}
                                                        </p>
                                                    )}
                                                </InputGroup>
                                            </CombineInputGroup>

                                            <InputGroup >
                                                <Label>
                                                    Manage Previous Dues *
                                                </Label>

                                                <Option
                                                    className={`${values.discardDues === true
                                                        ? "border-blue-500 bg-blue-100"
                                                        : "border-gray-300"
                                                        }`}
                                                    onClick={() => setFieldValue("discardDues", true)}
                                                >
                                                    <Radio
                                                        className={`${values.discardDues === true ? "bg-blue-500" : ""}`}
                                                    >
                                                        {values.discardDues === true && <InnerCircle />}
                                                    </Radio>
                                                    <span className="text-sm ml-3 text-gray-600">
                                                        Delete Previous Dues
                                                    </span>
                                                </Option>

                                                <Option
                                                    className={`${values.discardDues === false
                                                        ? "border-blue-500 bg-blue-100"
                                                        : "border-gray-300"
                                                        } mt-2`}
                                                    onClick={() => setFieldValue("discardDues", false)}
                                                >
                                                    <Radio
                                                        className={`${values.discardDues === false ? "bg-blue-500" : ""}`}
                                                    >
                                                        {values.discardDues === false && <InnerCircle />}
                                                    </Radio>
                                                    <span className="text-sm ml-3 text-gray-600">
                                                        Carry Forward
                                                    </span>
                                                </Option>

                                                {touched.discardDues && errors.discardDues && (
                                                    <p className="text-red-500 text-sm mt-1">
                                                        {errors.discardDues}
                                                    </p>
                                                )}
                                            </InputGroup>
                                        </Wrapper>
                                    </ScrollableContent>

                                    <ActionSection>
                                        <SideCancelBtn type="button" onClick={handleClose}>
                                            Cancel
                                        </SideCancelBtn>
                                        <SubmitBtn
                                            type="submit"
                                            disabled={reassignTenantLoading || isSubmitting || !selectedRoom}
                                        >
                                            {reassignTenantLoading || isSubmitting ? "Processing..." : "Reassign Tenant"}
                                        </SubmitBtn>
                                    </ActionSection>

                                    {/* Property Selection Modal */}
                                    <SinglePropSelectModal
                                        isOpen={showPropertyModal}
                                        onClose={() => setShowPropertyModal(false)}
                                        onSelect={handleSelectedProperty}
                                        options={propertyList}
                                        selectedProperty={selectedProperty}
                                    />

                                    {/* Room Selection Modal */}
                                    <SelectRoom
                                        isOpen={roomModalOpen}
                                        onClose={() => setRoomModalOpen(false)}
                                        onSelect={(room) =>
                                            handleSingleRoomSelect(room, setFieldValue)
                                        }
                                        options={roomList}
                                        selectedItem={selectedRoom}
                                        title="Select Room"
                                        searchPlaceholder="Search rooms..."
                                        idKey="id"
                                        nameKey="roomNum"
                                        descriptionKeys={["amenities"]}
                                        emptyMessage="No rooms available"
                                        noResultsMessage="No rooms found"
                                        showClearSelection={true}
                                    />
                                </Form>
                            );
                        }}
                    </Formik>
                </div>
            </Container>
        </>
    );
};

const Label = tw.label`text-sm text-gray-500 font-normal mb-1 block`;
const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-[#0078BD] py-4 px-4`;
const Option = tw.div`p-3 border rounded-lg flex items-center cursor-pointer mt-2`;
const Radio = tw.div`w-5 h-5 flex items-center justify-center rounded-full border border-gray-400`;
const InnerCircle = tw.div`w-3 h-3 bg-white rounded-full`;
export default ReassignTenant;